// src/components/studentPortal/MyProjects.jsx
// Section 2 of the Student Portal. Real data, existing endpoints only:
// getUserById -> getProjectsByUserId (+ getMembersByProjectId / showTools
// per project for the avatars/tool chip) + free-tier/status for the counter.
// "Create project" opens CreateProjectModal.jsx (new UI, existing backend).
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiFilm, FiPlus, FiUsers, FiFolder } from 'react-icons/fi';
import CreateProjectModal from './CreateProjectModal';
import { baseUrl } from '../../config';

const PROJECT_TYPE_LABEL = { short: 'Short Film', full: 'Feature Film', advertisement: 'Advertisement' };

const ProjectCard = ({ project }) => {
  const navigate = useNavigate();
  const [memberCount, setMemberCount] = useState(null);
  const [toolName, setToolName] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [membersRes, toolsRes] = await Promise.all([
          fetch(`${baseUrl}/getMembersByProjectId`, { headers: { project_id: String(project.project_id) } }),
          fetch(`${baseUrl}/showTools`, { headers: { project_id: String(project.project_id) } }),
        ]);
        if (membersRes.ok) setMemberCount((await membersRes.json()).length);
        if (toolsRes.ok) {
          const activeTools = await toolsRes.json();
          if (activeTools.length > 0) setToolName(activeTools[0].name);
        }
      } catch (_) {
        // Silent - card still renders without the member count / tool chip.
      }
    })();
  }, [project.project_id]);

  return (
    <div
      onClick={() => navigate(`/my-producer-buddy-studentportal/project/${project.project_id}`)}
      className="rounded-2xl border border-slate-200 bg-white p-4 hover:border-teal-300 hover:shadow-md transition-all duration-200 cursor-pointer"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white">
          <FiFilm size={18} />
        </div>
        {/* Member "avatars" - only user_id is available from getMembersByProjectId
            (no name/picture in that response), so this is a generic count
            badge rather than real per-person avatars. */}
        {memberCount != null && memberCount > 0 && (
          <span className="flex items-center gap-1 text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
            <FiUsers size={11} /> {memberCount}
          </span>
        )}
      </div>
      <h4 className="font-semibold text-slate-800 truncate">{project.title || 'Untitled project'}</h4>
      <p className="text-xs text-slate-500 mt-0.5">{PROJECT_TYPE_LABEL[project.project_type] || project.project_type || 'Project'}</p>
      <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full rounded-full bg-gradient-to-r from-teal-500 to-cyan-500 transition-all duration-500"
          style={{ width: `${Math.min(100, Math.max(0, project.progress || 0))}%` }}
        />
      </div>
      <div className="flex items-center justify-between mt-2">
        <p className="text-[11px] text-slate-400">{project.progress || 0}% complete</p>
        {toolName && (
          <span className="text-[11px] font-medium text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full truncate max-w-[100px]">
            {toolName}
          </span>
        )}
      </div>
    </div>
  );
};

// Mimics this section's own (richer) ProjectCard shape - icon+badge row,
// title, type line, progress bar, tool-chip row - not the simpler
// DashboardHome skeleton, since this card has more rows.
const ProjectCardSkeleton = () => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 animate-pulse">
    <div className="flex items-start justify-between mb-3">
      <div className="w-10 h-10 rounded-xl bg-slate-200" />
      <div className="w-10 h-5 rounded-full bg-slate-100" />
    </div>
    <div className="h-3.5 w-3/4 rounded bg-slate-200 mb-2" />
    <div className="h-2.5 w-1/2 rounded bg-slate-100 mb-4" />
    <div className="h-1.5 rounded-full bg-slate-100 mb-2" />
    <div className="h-2.5 w-1/3 rounded bg-slate-100" />
  </div>
);

const EmptyState = ({ onClick }) => (
  <button
    onClick={onClick}
    className="col-span-full rounded-2xl border-2 border-dashed border-slate-300 bg-white p-8 flex flex-col items-center gap-2 text-slate-500 hover:border-teal-400 hover:text-teal-600 transition-colors duration-200"
  >
    <FiFolder size={22} className="text-slate-300" />
    <span>No projects yet - click here to create your first one.</span>
  </button>
);

const CreateProjectCard = ({ onClick }) => (
  <button
    onClick={onClick}
    className="rounded-2xl border-2 border-dashed border-slate-300 bg-white p-4 min-h-[148px] flex flex-col items-center justify-center gap-2 text-slate-400 hover:border-teal-400 hover:text-teal-600 transition-colors duration-200"
  >
    <FiPlus size={22} />
    <span className="text-sm font-medium">Create project</span>
  </button>
);

const MyProjects = () => {
  const [projects, setProjects] = useState([]);
  const [projectsUsed, setProjectsUsed] = useState(null);
  const [projectsLimit, setProjectsLimit] = useState(8);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) { setLoading(false); return; }
    try {
      const userRes = await fetch(`${baseUrl}/getUserById`, { headers: { Authorization: `Bearer ${token}` } });
      if (!userRes.ok) return;
      const user = await userRes.json();

      const [projectsRes, statusRes] = await Promise.all([
        fetch(`${baseUrl}/getProjectsByUserId`, { headers: { Authorization: `Bearer ${token}`, user_id: String(user.user_id) } }),
        fetch(`${baseUrl}/free-tier/status`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (projectsRes.ok) setProjects(await projectsRes.json());
      if (statusRes.ok) {
        const status = await statusRes.json();
        setProjectsUsed(status.projects_used);
        setProjectsLimit(status.projects_limit || 8);
      }
    } catch (_) {
      // Fail quiet - an empty grid is fine, never blocks the page.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const atCap = projectsUsed != null && projectsUsed >= projectsLimit;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-slate-800">My Projects</h2>
        {projectsUsed != null && (
          <span className="text-sm font-medium text-slate-500">{projectsUsed} of {projectsLimit} used</span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => <ProjectCardSkeleton key={i} />)
        ) : projects.length === 0 ? (
          <EmptyState onClick={() => setShowModal(true)} />
        ) : (
          <>
            {projects.map((p) => <ProjectCard key={p.project_id} project={p} />)}
            {!atCap && <CreateProjectCard onClick={() => setShowModal(true)} />}
          </>
        )}
      </div>

      {atCap && (
        <p className="text-xs text-slate-400 mt-3">
          You've reached the free plan's {projectsLimit}-project limit.
        </p>
      )}

      {showModal && (
        <CreateProjectModal
          onClose={() => setShowModal(false)}
          onCreated={() => load()}
        />
      )}
    </div>
  );
};

export default MyProjects;
